import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getCurrentUser, fetchAuthSession, signIn, signOut, confirmSignIn } from "aws-amplify/auth";

const AuthContext = createContext(null);

// Map Cognito groups to application roles
function resolveRole(groups = []) {
  if (groups.includes("FleetAdmin")) return "FleetAdmin";
  if (groups.includes("ReleaseManager")) return "ReleaseManager";
  if (groups.includes("SupportEngineer")) return "SupportEngineer";
  return null;
}

// ─── DEV OVERRIDE ─────────────────────────────────────────────────────────────
// When REACT_APP_USE_MOCK_AUTH=true the app bypasses Cognito so you can develop
// the UI without a deployed backend. Set this to "false" once AWS is wired.
const USE_MOCK_AUTH = process.env.REACT_APP_USE_MOCK_AUTH === "true";

// ── Switch role here to test different access levels ──────────────────────────
// Options: "FleetAdmin" | "ReleaseManager" | "SupportEngineer"
const ACTIVE_ROLE = "ReleaseManager"; // "FleetAdmin" | "ReleaseManager" | "SupportEngineer"

const MOCK_USER = {
  sub: "sub-001",
  email: "admin@tetra.med",
  name: "Alice Admin",
  role: ACTIVE_ROLE,
  groups: [ACTIVE_ROLE],
};
// ──────────────────────────────────────────────────────────────────────────────

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadSession = useCallback(async () => {
    if (USE_MOCK_AUTH) {
      setUser(MOCK_USER);
      setRole(MOCK_USER.role);
      setLoading(false);
      return;
    }
    try {
      const cognitoUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const groups =
        session.tokens?.idToken?.payload["cognito:groups"] || [];
      const email =
        session.tokens?.idToken?.payload["email"] || cognitoUser.username;
      const name =
        session.tokens?.idToken?.payload["name"] || email;
      setUser({ sub: cognitoUser.userId, email, name, groups });
      setRole(resolveRole(groups));
    } catch {
      setUser(null);
      setRole(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const login = useCallback(async ({ email, password }) => {
    console.log('[Auth] login called, USE_MOCK_AUTH=', USE_MOCK_AUTH);
    if (USE_MOCK_AUTH) {
      setUser(MOCK_USER);
      setRole(MOCK_USER.role);
      return { isSignedIn: true };
    }
    console.log('[Auth] calling signIn for:', email);
    let result;
    try {
      result = await signIn({ username: email, password });
    } catch (err) {
      console.error('[Auth] signIn threw:', err);
      throw err;
    }
    console.log('[Auth] signIn result:', result);
    if (result.isSignedIn) {
      await loadSession();
    }
    // nextStep values handled by LoginPage:
    // - CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED  → set permanent password
    // - CONTINUE_SIGN_IN_WITH_TOTP_SETUP            → scan QR code in authenticator app
    // - CONFIRM_SIGN_IN_WITH_TOTP_CODE              → enter OTP from authenticator app
    // - CONFIRM_SIGN_IN_WITH_SMS_MFA                → enter SMS OTP
    return result;
  }, [loadSession]);

  // Called after NEW_PASSWORD_REQUIRED challenge
  const confirmNewPassword = useCallback(async (newPassword) => {
    const result = await confirmSignIn({ challengeResponse: newPassword });
    if (result.isSignedIn) {
      await loadSession();
    }
    return result;
  }, [loadSession]);

  // Called after SMS_MFA or TOTP_CODE challenge
  const confirmMfa = useCallback(async (otpCode) => {
    const result = await confirmSignIn({ challengeResponse: otpCode });
    if (result.isSignedIn) {
      await loadSession();
    }
    return result;
  }, [loadSession]);

  const logout = useCallback(async () => {
    if (USE_MOCK_AUTH) {
      setUser(null);
      setRole(null);
      return;
    }
    await signOut();
    setUser(null);
    setRole(null);
  }, []);

  const canAccess = useCallback(
    (...allowedRoles) => allowedRoles.includes(role),
    [role]
  );

  return (
    <AuthContext.Provider value={{ user, role, loading, login, logout, confirmNewPassword, confirmMfa, canAccess }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
